import { FeedbackMessage } from '@sendible/design-system/src/components/molecules/ComposeDropDown';
import { styled, css } from 'styled-components';

interface MediaBadgePropsInterface {
  position?: 'top_left' | 'bottom_left' | 'bottom_middle' | 'bottom_right';
  onClick?: React.MouseEventHandler<HTMLElement>;
  isIconHolder?: boolean;
}

export const Container = styled.div<{ $validation: FeedbackMessage['type'] }>`
  ${({
    theme: {
      breakPoints: { smallDevices },
      borderRadius,
      colors,
    },
    $validation,
  }) => css`
    border-radius: ${borderRadius.sm};
    position: relative;
    width: 100%;

    .dropdown-link {
      position: absolute;
      right: 0.5rem;
      top: 0.5rem;
    }

    .button {
      transition: all 0.1s ease-in-out;
      opacity: ${$validation ? 1 : 0};
      visibility: ${$validation ? 'visible' : 'hidden'};
      z-index: 1;
      ${
        $validation &&
        $validation === 'info' &&
        css`
          background: ${colors.brandDarkAlpha50};
        `
      }
    }

    &:hover {
      .button {
        margin-right: 0;
        opacity: 1;
        visibility: visible;
        ${
          $validation &&
          $validation === 'info' &&
          css`
            background: ${colors.brandDarkAlpha80};
          `
        }
      }
    }

    .show-dropdown-button {
      opacity: 1;
      visibility: visible;

      &:hover {
        .button {
          opacity: 1;
          visibility: visible;
        }
      }
    }

    @media all and (${smallDevices.min}) and (${smallDevices.max}) {
      .button {
        display: flex;
      }
    }
  `}
`;

export const Image = styled.div<{ $validation: FeedbackMessage['type'] }>`
  ${({ $validation, theme: { borderRadius, colors } }) => css`
    align-items: center;
    aspect-ratio: 1 / 1;
    background: ${colors.brandDarkTint90};
    border-radius: ${borderRadius.sm};
    display: flex;
    justify-content: center;
    position: relative;
    overflow: hidden;

    &::before {
      border-radius: inherit;
      box-shadow: inset 0 0 0 1px ${colors.brandDarkAlpha20};
      content: '';
      display: block;
      height: 100%;
      left: 0;
      pointer-events: none;
      position: absolute;
      top: 0;
      user-select: none;
      width: 100%;
      z-index: 1;
    }

    .placeholder {
      path {
        stroke: ${colors.brandDarkAlpha60};
      }
    }
    ${
      $validation &&
      $validation === 'danger' &&
      css`
        outline-color: ${colors.semanticDanger};
        outline-offset: 2px;
        outline-style: solid;
        outline-width: 2px;
      `
    }
    img,
    canvas {
      cursor: pointer;
      max-height: 100%;
      max-width: 100%;
      position: absolute;
      user-select: none;
    }
  `}
`;

export const BackgroundFiller = styled.button`
  background: transparent;
  border: 0;
  height: 100%;
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  padding: 0;
  margin: 0;
  z-index: 1;
`;

export const MediaBadge = styled.div<MediaBadgePropsInterface>`
  ${({ theme: { colors }, position = 'top_left', isIconHolder = false, onClick }) => css`
    appearance: none;
    border: 0;
    font-family: 'IBM Plex Sans';
    font-size: 0.75rem;
    font-style: normal;
    font-weight: 400;
    line-height: 1rem;
    letter-spacing: 0.3px;
    height: 1.125rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${colors.brandDarkAlpha90};
    position: absolute;
    z-index: 2;
    left: 0.5rem;
    user-select: none;

    svg {
      width: 1.1rem;
      height: 1.1rem;
      padding: 0;
      cursor: pointer;
    }

    ${
      position === 'top_left' &&
      css`
        width: 1.125rem;
        top: 0.5rem;
        border-radius: 0.75rem;
      `
    }

    ${
      position === 'bottom_left' &&
      css`
        width: auto;
        bottom: 0.5rem;
        border-radius: 0.25rem;
        padding: 0 0.25rem;
      `
    }

    ${
      position === 'bottom_middle' &&
      css`
        width: auto;
        bottom: 0.5rem;
        left: 50%;
        transform: translateX(-50%);
        border-radius: 0.25rem;
        padding: 0 0.25rem;
      `
    }


    ${
      position === 'bottom_right' &&
      css`
        width: auto;
        bottom: 0.5rem;
        left: auto;
        right: 0.5rem;
        border-radius: 0.25rem;
        padding: 0 0.25rem;
      `
    }

    ${
      onClick &&
      css`
        cursor: pointer;

        &:focus-visible {
          outline: 2px solid ${colors.brandLight};
          outline-offset: 2px;
        }
      `
    }
    color: ${colors.brandLight};
    text-align: center;
    ${
      isIconHolder &&
      css`
        padding: 0.125rem 0.01rem 0 0.07rem;
        height: 1rem;
      `
    }
  `}
`;

export const Details = styled.div`
  flex: 1;
  gap: 0.5rem;
  overflow: hidden;
  padding: 0.5rem 0;

  .text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
